import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { GlassCard } from "@/components/common/GlassCard";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/States";
import { StatusPill } from "@/components/common/StatusPill";
import { PlayerSubNav } from "@/components/common/PlayerSubNav";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Mic, Play, ChevronRight, TrendingUp, TrendingDown, Minus, Trophy,
  AlertCircle, Crosshair, Calendar, Sparkles, Trash2, RefreshCw,
} from "lucide-react";

const TYPE_OPTIONS = [
  { v: "combine", label: "Combine Interview", desc: "15-minute, multi-evaluator format" },
  { v: "team_formal", label: "Team Formal Interview", desc: "GM + position coach" },
  { v: "informal_coach", label: "Informal Coach Conversation", desc: "Relaxed, exploratory" },
  { v: "position_specific", label: "Position-Specific", desc: "Scheme + film deep-dive" },
  { v: "character", label: "Character / Background", desc: "Off-field, family, leadership" },
  { v: "injury_adversity", label: "Injury / Adversity", desc: "How you responded to setbacks" },
];

const DIFFICULTY_OPTIONS = [
  { v: "basic", label: "Basic", desc: "Supportive, surface-level" },
  { v: "standard", label: "Standard", desc: "Realistic, expects substantive answers" },
  { v: "high_pressure", label: "High Pressure", desc: "Multiple follow-ups, evaluator skepticism" },
];

const DIM_LABELS = {
  clarity: "Clarity",
  confidence: "Confidence",
  authenticity: "Authenticity",
  accountability: "Accountability",
  football_iq: "Football IQ",
  presence: "Presence",
};

const TYPE_LABEL_MAP = Object.fromEntries(TYPE_OPTIONS.map((t) => [t.v, t.label]));

function useResolvedPlayerId() {
  const { user } = useAuth();
  const { playerId } = useParams();
  return playerId || user?.player_id;
}

function TrendIcon({ trend }) {
  if (trend === "up") return <TrendingUp className="h-4 w-4 text-emerald-300" data-testid="trend-up" />;
  if (trend === "down") return <TrendingDown className="h-4 w-4 text-rose-300" data-testid="trend-down" />;
  return <Minus className="h-4 w-4 text-3" data-testid="trend-stable" />;
}

function ScoreMeter({ value, max = 10, label }) {
  const v = Number(value || 0);
  const pct = Math.max(0, Math.min(100, (v / max) * 100));
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-3 text-[10px] uppercase tracking-wider">{label}</span>
        <span className={`font-mono text-sm ${v >= 8 ? "text-emerald-300" : v >= 6 ? "text-cyan-200" : v >= 4 ? "text-amber-200" : "text-rose-200"}`}>{v}/10</span>
      </div>
      <div className="h-1.5 w-full rounded bg-white/5 overflow-hidden">
        <div className="h-full rounded transition-all" style={{ width: `${pct}%`, background: v >= 8 ? "#86efac" : v >= 6 ? "#7dd3fc" : v >= 4 ? "#fcd34d" : "#fda4af" }} />
      </div>
    </div>
  );
}

function HeroOverall({ dash }) {
  const score = dash?.overall_score || 0;
  const trend = dash?.overall_trend || "stable";
  return (
    <GlassCard className="col-span-12 md:col-span-6 lg:col-span-4 p-6 relative overflow-hidden" data-testid="ip-hero-overall">
      <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(125,211,252,0.18), transparent 70%)" }} />
      <div className="text-3 text-[10px] uppercase tracking-wider">Last interview score</div>
      <div className="mt-2 flex items-end gap-3">
        <div className="text-1 text-5xl font-semibold font-mono leading-none">{score || "—"}</div>
        <div className="text-3 text-sm pb-1">/10</div>
        <div className="ml-auto flex items-center gap-1 text-xs text-2"><TrendIcon trend={trend} />{trend === "up" ? "Improving" : trend === "down" ? "Declining" : "Steady"}</div>
      </div>
      <div className="text-3 text-[11px] mt-2">{dash?.last_session ? `From ${TYPE_LABEL_MAP[dash.last_session.type] || dash.last_session.type} · ${new Date(dash.last_session.completed_at || dash.last_session.started_at).toLocaleDateString()}` : "No completed sessions yet"}</div>
    </GlassCard>
  );
}

function DimensionCard({ dim, label, data }) {
  const score = data?.score || 0;
  const trend = data?.trend || "stable";
  return (
    <div className="glass p-3" data-testid={`ip-dim-${dim}`}>
      <div className="flex items-center justify-between">
        <span className="text-3 text-[10px] uppercase tracking-wider">{label}</span>
        <TrendIcon trend={trend} />
      </div>
      <div className="mt-1 flex items-end gap-1">
        <span className={`font-mono text-xl ${score >= 8 ? "text-emerald-300" : score >= 6 ? "text-cyan-200" : score >= 4 ? "text-amber-200" : "text-rose-200"}`}>{score || "—"}</span>
        <span className="text-3 text-[11px] pb-0.5">/10</span>
      </div>
      <div className="mt-2 h-1 w-full rounded bg-white/5 overflow-hidden">
        <div className="h-full rounded" style={{ width: `${(score / 10) * 100}%`, background: score >= 8 ? "#86efac" : score >= 6 ? "#7dd3fc" : score >= 4 ? "#fcd34d" : "#fda4af" }} />
      </div>
    </div>
  );
}

function DimensionsCard({ dash }) {
  const dims = dash?.dimensions || {};
  return (
    <GlassCard className="col-span-12 md:col-span-6 lg:col-span-8 p-5" data-testid="ip-dims-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-3 text-[10px] uppercase tracking-wider">6-dimension breakdown</div>
          <div className="text-1 text-base font-semibold mt-0.5">Latest session evaluator scores</div>
        </div>
        <Sparkles className="h-4 w-4 text-violet-300" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {Object.entries(DIM_LABELS).map(([k, label]) => (
          <DimensionCard key={k} dim={k} label={label} data={dims[k]} />
        ))}
      </div>
    </GlassCard>
  );
}

function FocusAreasCard({ dash }) {
  const focus = dash?.recommended_focus || [];
  return (
    <GlassCard className="col-span-12 md:col-span-6 lg:col-span-4 p-5" data-testid="ip-focus-card">
      <div className="text-3 text-[10px] uppercase tracking-wider flex items-center gap-1.5"><Crosshair className="h-3 w-3" /> Recommended focus areas</div>
      <div className="text-1 text-base font-semibold mt-0.5">Where to push next</div>
      {focus.length === 0 ? (
        <div className="text-3 text-[12px] mt-3">Complete a session to unlock focus areas.</div>
      ) : (
        <div className="mt-3 flex flex-col gap-2">
          {focus.map((k) => (
            <div key={k} className="glass p-2.5 flex items-center gap-2" data-testid={`ip-focus-${k}`}>
              <Crosshair className="h-3.5 w-3.5 text-amber-300" />
              <span className="text-2 text-sm">{DIM_LABELS[k] || k}</span>
              <span className="ml-auto text-3 text-[11px]">{dash.dimensions?.[k]?.score || 0}/10</span>
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  );
}

function CountsCard({ dash }) {
  const completed = dash?.total_sessions_completed || 0;
  const total = dash?.total_sessions_all || 0;
  const last5 = dash?.spark_overall_last5 || [];
  const max = Math.max(10, ...last5);
  return (
    <GlassCard className="col-span-12 md:col-span-6 lg:col-span-4 p-5" data-testid="ip-counts-card">
      <div className="text-3 text-[10px] uppercase tracking-wider">Sessions completed</div>
      <div className="mt-1 flex items-end gap-2">
        <span className="font-mono text-3xl text-1">{completed}</span>
        <span className="text-3 text-[12px] pb-1">of {total} total</span>
      </div>
      <div className="text-3 text-[11px] mt-3 mb-1.5">Last 5 overall scores</div>
      {last5.length === 0 ? (
        <div className="text-3 text-[11px]">No sessions yet.</div>
      ) : (
        <div className="flex items-end gap-1 h-12">
          {last5.map((v, i) => (
            <div key={i} className="flex-1 rounded-t bg-cyan-300/30 hover:bg-cyan-300/50 transition" style={{ height: `${(v / max) * 100}%` }} title={`${v}/10`} />
          ))}
        </div>
      )}
    </GlassCard>
  );
}

function SessionRow({ s, onOpen, onAbandon, isAdmin, isPlayer }) {
  const status = s.status;
  const tone = status === "completed" ? "violet" : status === "active" ? "cyan" : "neutral";
  const date = s.completed_at || s.started_at;
  return (
    <div className="glass p-3 flex items-center gap-3 hover:ring-1 hover:ring-cyan-300/30 transition" data-testid={`ip-session-row-${s.id}`}>
      <div className="flex flex-col items-start gap-1 min-w-[140px]">
        <span className="text-1 text-sm font-semibold">{TYPE_LABEL_MAP[s.type] || s.type}</span>
        <StatusPill tone={tone}>{status}</StatusPill>
      </div>
      <div className="text-3 text-[11px] capitalize w-24">{(s.difficulty || "").replace(/_/g, " ")}</div>
      <div className="flex-1 flex items-center gap-2">
        <Calendar className="h-3 w-3 text-3" />
        <span className="text-3 text-[11px]">{date ? new Date(date).toLocaleString() : "—"}</span>
      </div>
      {status === "completed" && (
        <div className="flex items-center gap-3">
          <span className="text-2 font-mono text-base">{s.overall_score || "—"}<span className="text-3 text-[10px]">/10</span></span>
        </div>
      )}
      <Button variant="ghost" size="sm" onClick={() => onOpen(s)} className="text-2 hover:text-white" data-testid={`ip-session-open-${s.id}`}>
        {status === "active" ? "Resume" : "Review"} <ChevronRight className="h-3 w-3" />
      </Button>
      {(isAdmin || isPlayer) && status === "active" && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onAbandon(s)}
          className="text-rose-200 hover:text-rose-100"
          data-testid={`ip-session-abandon-${s.id}`}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

function StartSessionDialog({ open, onOpenChange, onStart, busy }) {
  const [type, setType] = useState("team_formal");
  const [difficulty, setDifficulty] = useState("standard");
  const [turns, setTurns] = useState(6);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 max-w-xl text-1" data-testid="ip-start-dialog">
        <DialogHeader>
          <DialogTitle>Start a new interview session</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-3 text-[11px] uppercase tracking-wider">Step 1 — Interview type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="bg-white/5 border-white/10 text-1 mt-1.5" data-testid="ip-select-type"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10 text-1">
                {TYPE_OPTIONS.map((t) => (
                  <SelectItem key={t.v} value={t.v} data-testid={`ip-type-${t.v}`}>
                    <div className="flex flex-col">
                      <span className="text-1">{t.label}</span>
                      <span className="text-3 text-[11px]">{t.desc}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-3 text-[11px] uppercase tracking-wider">Step 2 — Difficulty</Label>
            <Select value={difficulty} onValueChange={setDifficulty}>
              <SelectTrigger className="bg-white/5 border-white/10 text-1 mt-1.5" data-testid="ip-select-difficulty"><SelectValue /></SelectTrigger>
              <SelectContent className="glass-strong border-white/10 text-1">
                {DIFFICULTY_OPTIONS.map((d) => (
                  <SelectItem key={d.v} value={d.v} data-testid={`ip-diff-${d.v}`}>
                    <div className="flex flex-col">
                      <span className="text-1">{d.label}</span>
                      <span className="text-3 text-[11px]">{d.desc}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-3 text-[11px] uppercase tracking-wider">Step 3 — Number of questions</Label>
            <div className="flex items-center gap-2 mt-1.5">
              {[3, 5, 6, 8, 10].map((n) => (
                <Button
                  key={n}
                  variant="ghost"
                  onClick={() => setTurns(n)}
                  className={`px-3 py-1 text-sm ${turns === n ? "ring-1 ring-cyan-300/50 bg-cyan-300/10 text-1" : "text-3"}`}
                  data-testid={`ip-turns-${n}`}
                >
                  {n}
                </Button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-2">Cancel</Button>
          <Button
            data-testid="ip-start-confirm"
            onClick={() => onStart({ type, difficulty, target_turns: turns })}
            disabled={busy}
            className="cta-primary"
          >
            <Play className="h-4 w-4" /> {busy ? "Starting…" : "Start interview"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function InterviewPrepPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const playerId = useResolvedPlayerId();
  const isAdmin = user?.role === "admin";
  const isPlayer = user?.role === "player";

  const [dash, setDash] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startOpen, setStartOpen] = useState(false);
  const [starting, setStarting] = useState(false);

  const load = async () => {
    if (!playerId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        api.get("/interview/dashboard", { params: { player_id: playerId } }),
        api.get("/interview/sessions", { params: { player_id: playerId } }),
      ]);
      setDash(d.data);
      setSessions(s.data || []);
    } catch (e) { toast.error("Failed to load interview prep"); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [playerId]);

  const startSession = async (cfg) => {
    setStarting(true);
    try {
      const body = { ...cfg };
      if (isAdmin) body.player_id = playerId;
      const r = await api.post("/interview/sessions", body);
      const sid = r.data.session?.id;
      if (!sid) throw new Error("No session id");
      setStartOpen(false);
      navigate(`./sessions/${sid}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start session");
    } finally { setStarting(false); }
  };

  const abandonSession = async (s) => {
    if (!window.confirm(`Abandon this ${TYPE_LABEL_MAP[s.type] || s.type} session?`)) return;
    try {
      await api.delete(`/interview/sessions/${s.id}`);
      setSessions((arr) => arr.filter((x) => x.id !== s.id));
      toast.success("Session abandoned");
    } catch { toast.error("Failed"); }
  };

  const openSession = (s) => navigate(`./sessions/${s.id}`);

  const active = useMemo(() => sessions.filter((s) => s.status === "active"), [sessions]);
  const completed = useMemo(() => sessions.filter((s) => s.status === "completed"), [sessions]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI Interview Simulator"
        subtitle="Realistic NFL evaluator-driven interviews with 6-dimension scoring."
        actions={(
          <Button
            data-testid="ip-start-btn"
            onClick={() => setStartOpen(true)}
            className="cta-primary"
          >
            <Mic className="h-4 w-4" /> Start new interview
          </Button>
        )}
      />
      <PlayerSubNav playerId={playerId} />

      {loading ? <LoadingState /> : (
        <>
          <div className="grid grid-cols-12 gap-5">
            <HeroOverall dash={dash} />
            <DimensionsCard dash={dash} />
            <CountsCard dash={dash} />
            <FocusAreasCard dash={dash} />
            <GlassCard className="col-span-12 md:col-span-6 lg:col-span-4 p-5" data-testid="ip-last-summary-card">
              <div className="text-3 text-[10px] uppercase tracking-wider flex items-center gap-1.5"><Trophy className="h-3 w-3" /> Last session summary</div>
              {dash?.last_session ? (
                <div className="space-y-3 mt-2">
                  {(dash.last_session.biggest_strengths || []).slice(0, 2).map((s, i) => (
                    <div key={i} className="text-2 text-[12.5px] flex gap-2">
                      <Trophy className="h-3 w-3 text-emerald-300 mt-0.5 shrink-0" /> {s}
                    </div>
                  ))}
                  {(dash.last_session.biggest_concerns || []).slice(0, 2).map((s, i) => (
                    <div key={i} className="text-2 text-[12.5px] flex gap-2">
                      <AlertCircle className="h-3 w-3 text-rose-300 mt-0.5 shrink-0" /> {s}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-3 text-[12px] mt-3">Run your first session to see strengths and concerns.</div>
              )}
            </GlassCard>
          </div>

          {active.length > 0 && (
            <GlassCard className="p-4" data-testid="ip-active-list">
              <div className="text-3 text-[10px] uppercase tracking-wider mb-2">Active sessions</div>
              <div className="flex flex-col gap-2">
                {active.map((s) => (
                  <SessionRow key={s.id} s={s} onOpen={openSession} onAbandon={abandonSession} isAdmin={isAdmin} isPlayer={isPlayer} />
                ))}
              </div>
            </GlassCard>
          )}

          <GlassCard className="p-4" data-testid="ip-history-list">
            <div className="flex items-center justify-between mb-2">
              <div className="text-3 text-[10px] uppercase tracking-wider">Interview history</div>
              <Button variant="ghost" size="sm" onClick={load} className="text-3 hover:text-white"><RefreshCw className="h-3 w-3" /></Button>
            </div>
            {completed.length === 0 ? (
              <EmptyState title="No completed sessions yet" description="Start your first interview to begin building your evaluation profile." />
            ) : (
              <div className="flex flex-col gap-2">
                {completed.map((s) => (
                  <SessionRow key={s.id} s={s} onOpen={openSession} onAbandon={abandonSession} isAdmin={isAdmin} isPlayer={isPlayer} />
                ))}
              </div>
            )}
          </GlassCard>
        </>
      )}

      <StartSessionDialog open={startOpen} onOpenChange={setStartOpen} onStart={startSession} busy={starting} />
    </div>
  );
}
